import { useRef, useState, useEffect } from 'react'
import useAuth from '../../hooks/useAuth'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import axios from '../../api/axios'
import { useDispatch } from 'react-redux'
import {
    setInitData,
    setLoggedInUser,
} from '../../features/security/security.slice'
import { Messages } from 'primereact/messages'
import AppInitiatedDialog from '../common/dialogs/AppInitiatedDialog'
import fullLogoLight from '../../client/logo/logo-full-light.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

const LOGIN_URL = '/auth/login'

const Login = () => {
    const [initiated, setInitiated] = useState(true)
    const { setAuth }: any = useAuth()

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const from = location.state?.from?.pathname || '/'

    const usernameRef = useRef()
    const errRef = useRef()

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [isLoggingIn, setIsLoggingIn] = useState(false)
    const [errMsg, setErrMsg] = useState('')
    const msgs = useRef(null)

    useEffect(() => {
        setErrMsg('')
    }, [username, password])

    useEffect(() => {
        if (errMsg) {
            msgs.current.show({
                id: '1',
                sticky: true,
                severity: 'error',
                summary: '',
                detail: errMsg,
                closable: false,
            })
        } else {
            msgs.current.clear()
        }
    }, [errMsg])

    const handleSubmit = async (e) => {
        e.preventDefault()

        setErrMsg('')
        setIsLoggingIn(true)

        try {
            const response = await axios
                .post(LOGIN_URL, JSON.stringify({ username, password }), {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                })
                .finally(() => {
                    setIsLoggingIn(false)
                })
            const accessToken = response?.data?.data.token
            const user = response?.data?.data.user
            setAuth({ username, password, user, accessToken })
            dispatch(setInitData(response.data?.data?.initData))
            dispatch(setLoggedInUser(response.data?.data?.user))
            setUsername('')
            setPassword('')
            navigate(from, { replace: true })
        } catch (err) {
            if (typeof err?.response?.data?.initiated !== 'undefined') {
                setInitiated(err?.response?.data?.initiated)
            } else {
                setInitiated(true)
            }

            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 400) {
                setErrMsg('Missing Username or Password')
            } else if (
                err.response?.status === 401 ||
                err.response?.status === 403
            ) {
                setErrMsg(err.response.data.errors.join('<br />'))
            } else {
                setErrMsg('Login Failed')
            }
        }
    }

    return (
        <section className="bg-gradient-primary vh-100">
            {initiated === false ? <AppInitiatedDialog /> : <></>}
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-xl-10 col-lg-12 col-md-9">
                        <div className="card o-hidden border-0 shadow-lg my-5">
                            <div className="card-body p-0">
                                <div className="row">
                                    <div className="col-lg-6 d-none d-lg-block bg-login-image" />
                                    <div className="col-lg-6">
                                        <div className="p-5">
                                            <div className="text-center">
                                                <div className="p-4 text-center w-100 border-bottom border-primary mb-4">
                                                    <img
                                                        src={fullLogoLight}
                                                        alt="Full Logo"
                                                        className="full-logo"
                                                    />
                                                </div>
                                                <h1 className="h4 mb-4">
                                                    Welcome Back!
                                                </h1>
                                                <div
                                                    ref={errRef}
                                                    className={
                                                        'text-danger small ' +
                                                        (errMsg
                                                            ? 'errmsg'
                                                            : 'offscreen')
                                                    }
                                                    aria-live="assertive"
                                                >
                                                    <Messages ref={msgs} />
                                                </div>
                                            </div>
                                            <form
                                                className="user"
                                                onSubmit={handleSubmit}
                                            >
                                                <div className="form-group mb-3">
                                                    <input
                                                        type="text"
                                                        ref={usernameRef}
                                                        className="form-control form-control-user p-3"
                                                        id="username"
                                                        aria-describedby="usernameHelp"
                                                        placeholder="Enter Username..."
                                                        onChange={(e) =>
                                                            setUsername(
                                                                e.target.value
                                                            )
                                                        }
                                                        value={username}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group mb-3">
                                                    <input
                                                        type="password"
                                                        className="form-control form-control-user p-3"
                                                        id="password"
                                                        placeholder="Password"
                                                        onChange={(e) =>
                                                            setPassword(
                                                                e.target.value
                                                            )
                                                        }
                                                        value={password}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group mb-3">
                                                    <div className="custom-control custom-checkbox small">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="customCheck"
                                                        />
                                                        <label
                                                            className="custom-control-label ms-2"
                                                            htmlFor="customCheck"
                                                        >
                                                            Remember Me
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="text-center d-grid gap-2">
                                                    <button
                                                        className="btn btn-primary btn-user d-block"
                                                        disabled={isLoggingIn}
                                                    >
                                                        {isLoggingIn ? (
                                                            <span>
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faCircleNotch
                                                                    }
                                                                    spin={true}
                                                                />{' '}
                                                                Logging in...
                                                            </span>
                                                        ) : (
                                                            'Login'
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                            <div className="text-center mt-3">
                                                <Link
                                                    to={'/forgot-password'}
                                                    className="small"
                                                >
                                                    Forgot Password?
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Login
